<template>
  <section 
    id="intro" 
    class="section hero" 
    aria-labelledby="hero-title"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
    ref="heroSection"
  >
    <div class="grid-bg" aria-hidden="true"></div>
    <div class="stars" aria-hidden="true"></div>
    <div class="cursor-trail" ref="cursorTrail"></div>
    <div class="cursor-glow" ref="cursorGlow"></div>
    <div class="content">
      <h1 id="hero-title" class="glow">{{ t('hero.title') }}</h1>
      <p class="subtitle">{{ t('hero.subtitle') }}</p>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const heroSection = ref(null)
const cursorTrail = ref(null)
const cursorGlow = ref(null)

let trailParticles = []
let animationId = null

const handleMouseMove = (event) => {
  const rect = heroSection.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  
  // Update cursor glow position
  if (cursorGlow.value) {
    cursorGlow.value.style.left = `${x}px`
    cursorGlow.value.style.top = `${y}px`
    cursorGlow.value.style.opacity = '0.6'
  }
  
  // Add trail particle
  if (Math.random() > 0.3) { // Only create particles 70% of the time for subtlety
    createTrailParticle(x, y)
  }
}

const handleMouseLeave = () => {
  if (cursorGlow.value) {
    cursorGlow.value.style.opacity = '0'
  }
}

const createTrailParticle = (x, y) => {
  const particle = document.createElement('div')
  particle.className = 'trail-particle'
  particle.style.left = `${x}px`
  particle.style.top = `${y}px`
  
  if (cursorTrail.value) {
    cursorTrail.value.appendChild(particle)
    
    trailParticles.push({
      element: particle,
      x: x,
      y: y,
      life: 1.0,
      size: Math.random() * 4 + 2
    })
    
    // Clean up old particles
    if (trailParticles.length > 50) {
      const oldParticle = trailParticles.shift()
      if (oldParticle.element.parentNode) {
        oldParticle.element.parentNode.removeChild(oldParticle.element)
      }
    }
  }
}

const animateTrail = () => {
  trailParticles.forEach((particle, index) => {
    particle.life -= 0.02
    particle.y -= 0.5
    particle.x += (Math.random() - 0.5) * 0.5
    
    if (particle.life <= 0) {
      if (particle.element.parentNode) {
        particle.element.parentNode.removeChild(particle.element)
      }
      trailParticles.splice(index, 1)
    } else {
      particle.element.style.opacity = particle.life
      particle.element.style.transform = `translate(-50%, -50%) scale(${particle.life})`
      particle.element.style.left = `${particle.x}px`
      particle.element.style.top = `${particle.y}px`
    }
  })
  
  animationId = requestAnimationFrame(animateTrail)
}

onMounted(() => {
  animateTrail()
})

onUnmounted(() => {
  if (animationId) {
    cancelAnimationFrame(animationId)
  }
})
</script>

<style scoped>
.section.hero {
  position: relative; z-index: 0;
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 4rem 1.5rem;
}
.section.hero::before {
  /* Vibrant animated blue aurora background */
  content: '';
  position: absolute; inset: -30%; z-index: -1; pointer-events: none;
  background: radial-gradient(50% 70% at 15% 25%, rgba(96,165,250,0.65), transparent 55%),
              radial-gradient(60% 60% at 85% 15%, rgba(37,99,235,0.60), transparent 60%),
              radial-gradient(55% 55% at 50% 90%, rgba(59,130,246,0.50), transparent 60%),
              radial-gradient(40% 40% at 70% 50%, rgba(147,197,253,0.35), transparent 50%);
  filter: saturate(1.5) contrast(1.1) blur(0px);
  animation: aurora 14s ease-in-out infinite alternate;
  mix-blend-mode: lighten;
}
.content { text-align: center; max-width: 960px; }
.glow {
  font-size: clamp(2rem, 6vw, 4rem);
  color: #f0f7ff;
  text-shadow: 0 0 10px rgba(96, 165, 250, 0.8), 0 0 30px rgba(59, 130, 246, 0.6), 0 0 60px rgba(37, 99, 235, 0.4);
  letter-spacing: 0.5px;
  animation: float 6s ease-in-out infinite;
}
.subtitle {
  margin-top: 1rem;
  color: #a6b6ff;
  font-size: clamp(1rem, 2.5vw, 1.4rem);
}
.cta {
  display: inline-block;
  margin-top: 2rem;
  padding: 0.9rem 1.4rem;
  border-radius: 12px;
  background: linear-gradient(135deg,#60a5fa,#2563eb);
  color: white;
  text-decoration: none;
  transition: transform .2s ease, box-shadow .2s ease;
  box-shadow: 0 8px 24px rgba(59, 130, 246, .35);
}
.cta:hover { transform: translateY(-2px) scale(1.02); box-shadow: 0 12px 30px rgba(37,99,235,.40); }

/* Backgrounds */
.grid-bg::before {
  content: '';
  position: absolute;
  inset: -50% -50% -50% -50%;
  background: radial-gradient( circle at 50% 50%, rgba(37,99,235,0.30), transparent 50%),
              radial-gradient( circle at 20% 80%, rgba(96,165,250,0.28), transparent 40%),
              linear-gradient( rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 100% 100%, 100% 100%, 64px 64px, 64px 64px;
  background-position: center, center, 0 0, 0 0;
  filter: drop-shadow(0 0 50px rgba(37,99,235,.15));
  animation: drift 30s linear infinite;
}
.stars::before, .stars::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: radial-gradient(2.5px 2.5px at 20% 30%, rgba(255,255,255,0.85), transparent),
                    radial-gradient(2px 2px at 70% 60%, rgba(147,197,253,0.8), transparent),
                    radial-gradient(1.5px 1.5px at 40% 80%, rgba(255,255,255,0.7), transparent),
                    radial-gradient(2px 2px at 85% 25%, rgba(96,165,250,0.75), transparent),
                    radial-gradient(1.5px 1.5px at 10% 70%, rgba(255,255,255,0.65), transparent);
  animation: twinkle 8s ease-in-out infinite;
}
.stars::after { animation-delay: -4s; }

@keyframes drift { from{ transform: rotate(0deg) scale(1); } to{ transform: rotate(360deg) scale(1.1);} }
@keyframes twinkle { 0%,100%{ opacity:.5 } 50%{ opacity:1 } }
@keyframes float { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-8px) } }
@keyframes aurora {
  0% { transform: translate3d(-4%, -3%, 0) scale(1.05); filter: hue-rotate(0deg) saturate(1.5) brightness(1.0); }
  33% { transform: translate3d(3%, 4%, 0) scale(1.12); filter: hue-rotate(15deg) saturate(1.7) brightness(1.1); }
  66% { transform: translate3d(-2%, 2%, 0) scale(1.08); filter: hue-rotate(-10deg) saturate(1.6) brightness(1.05); }
  100% { transform: translate3d(2%, -2%, 0) scale(1.06); filter: hue-rotate(5deg) saturate(1.5) brightness(1.0); }
}

/* Cursor animations */
.cursor-trail {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.cursor-glow {
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(96, 165, 250, 0.35) 0%, rgba(37, 99, 235, 0.20) 40%, transparent 70%);
  pointer-events: none;
  z-index: 1;
  transform: translate(-50%, -50%);
  transition: opacity 0.3s ease;
  opacity: 0;
  filter: blur(1px);
}

.trail-particle {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(96, 165, 250, 0.7) 50%, transparent 100%);
  pointer-events: none;
  z-index: 1;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.6);
}

@media (prefers-reduced-motion: reduce) {
  .section.hero::before { animation: none; }
  .cursor-glow, .trail-particle { display: none; }
}
</style>
